import { assign, omit, pick } from 'lodash';
import { createSelector } from 'reselect';
import { modules } from '@veritone/glc-redux';
import { selectApplicationConfigs } from '../app';

const {
  config: { getConfig },
} = modules;

export const selectConfig = (state: any) => getConfig<Window['config']>(state);

export const selectConfigEngines = createSelector(
  selectConfig,
  selectApplicationConfigs,
  (config, applicationConfigs) => {
    const transcriptionEngines = applicationConfigs.transcriptionEngines as
      | Record<string, string>
      | null
      | undefined;
    const transcriptionEngineId = applicationConfigs.transcriptionEngineId as
      | string
      | undefined;
    const voiceMaskEngineId = applicationConfigs.voiceMaskEngineId as
      | string
      | undefined;
    const speakerSeparationEngineId =
      applicationConfigs.speakerSeparationEngineId as string | undefined;

    let detectionEngineGPU = applicationConfigs.detectionEngineGPU as
      | string
      | undefined;

    // for some reason can't use the selectFeatureFlags selector
    const featureFlags = config?.featureFlags || {};
    const appConfigFeatureFlagsJSON = (applicationConfigs.featureFlags ||
      {}) as Record<string, boolean | number>;
    const useGPU =
      featureFlags.useGPU ||
      appConfigFeatureFlagsJSON.useGPU ||
      applicationConfigs.useGPU;

    // might be cleaner way to code this
    const applicationConfigOverrides = {
      ...(!!transcriptionEngineId && { transcriptionEngineId }), // not sure we actually need this given transcriptionEngines now being used?
      ...(!!transcriptionEngines && { transcriptionEngines }),
      ...(!!voiceMaskEngineId && { voiceMaskEngineId }),
      ...(!!speakerSeparationEngineId && { speakerSeparationEngineId }),
      ...(!!detectionEngineGPU && { detectionEngineGPU }),
    };

    const transcriptionEngineOptions = omit(
      // read the value directly — pick() would return a { transcriptionEngineOptions: ... }
      // wrapper, leaving the options double-nested and this omit() a no-op (VE-24980)
      (applicationConfigs.transcriptionEngineOptions as
        | Record<string, unknown>
        | null
        | undefined) || {},
      // remove keys that should not be overwritten
      'app',
      'user',
      'organizationId'
    );

    const combined = assign(
      {
        outputWriterEngineId: '8eccf9cc-6b6d-4d7d-8cb3-7ebf4950c5f3',
        // legacy ingestion engines - keep for now to support old TDOs
        webstreamAdapterEngineId: '9e611ad7-2d3b-48f6-a51b-0a1ba40fe255',
        streamIngestorEngineId: '8bdb0e3b-ff28-4f6e-a3ba-887bd06e6440',
        streamIngestorDASHPlaybackSegmentCreatorId:
          '29694232-a4f5-4622-a422-b48f206ca52a',
        streamIngestorPlaybackSegmentCreatorId:
          '352556c7-de07-4d55-b33f-74b1cf237f25',
        // legacy redaction engine
        legacyRedactEngineId: '51263b9d-186b-4d09-a422-a8f12cc38f5c',
        // new engines
        voiceMaskEngineId: '9387281c-0f99-4453-a91f-1cc2449f2c99', // hard code default since no longer using data-center config (can override engine id using appConfig)
        /**
         * Speaker separation. Speechmatics Speaker Separation Enhanced
         * (v12.0.1) - English: the only one provisioned in the test
         * environment, so it is the default while the engine choice is being
         * settled. An org overrides it with the `speakerSeparationEngineId`
         * appConfig.
         *
         * Candidates to move to, all English and all from
         * engines/aiware/transcribe/speechmatics-chunk/cicd/cicd-engines.json:
         *   1085ea00-4b85-4640-8991-ceb1291ad9f4  v12.0.1 standard
         *       (same version as this one, without the enhanced tier —
         *        cicdEnabled is false on it, worth checking before use)
         *   2a76b2e4-5c61-4b7e-8bda-8c2298fc3798  v13.4.0 standard
         *   5af27bf4-c163-42d3-b121-0c5336a4978c  v14.13.0 standard
         *       (the intended upgrade; the enhanced v14 is
         *        e9975cdf-1721-4946-bf9b-87812e254b96, which needs timing
         *        comparisons to justify over standard)
         *
         * Previously Veritone Speaker Separation V3F,
         * veritone/task-veritone-speaker-detection,
         * 02f4d710-cbcd-4534-98d6-a31522f8f4a6.
         *
         * speakerSeparationPayload in helpers/speakerSeparationDag.ts sends
         * whichever parameters the configured engine actually reads, so
         * changing this id is enough to switch engines.
         */
        speakerSeparationEngineId: 'cbfa6c21-ad7a-4fba-a6c4-d6d933b04e2d',
        detectionEngineGPU: '4949f413-f904-4771-bf25-c00e424de001', // hard code
        // Alternate - Image ingestion DAG engines (not used by default)
        si2EngineId: '8bdb0e3b-ff28-4f6e-a3ba-887bd06e6440',
        imageTranscodeEngineId: '8c0c2d4e-eedf-48ff-8b94-a26242e683cf',
        thumbnailGeneratorEngineId: 'fd891cb5-ff80-48ae-b5cd-ac3c7d190d55',
        /**
         * "speaker detection" category. Results are read back by category id
         * rather than engine id so that separation produced by either engine,
         * or by whichever one the config pointed at previously, still loads.
         *
         * The category is SHARED: it also covers Speaker Recognition V3F. That
         * makes membership of it a weaker claim than "this is speaker
         * separation output", and the app treats the two as the same thing -
         * loading, the running/failed indicators, and deleting all key off the
         * category alone. Safe only because Redact never runs recognition:
         * nothing in this repo creates a job for it, and the product does not
         * offer it. If that changes, these call sites need a discriminator
         * before the category can keep standing in for identity.
         */
        speakerDetectionCategoryId: 'a856c447-1030-4fb0-917f-08179f949c4e',
        // PDF converter engine for document ingestion - hard code default so it
        // works even when not present in data-center config (can override using appConfig)
        pdfConverterEngineId: 'eda09a05-2bbc-490a-96b6-3768875be88d',
        // Document PII detection engine - runs after the PDF converter when the
        // user opts in (FilePicker "Run PII" checkbox / re-run button). Hard code
        // default so it works without a data-center config entry (can override
        // using appConfig).
        documentPIIDetectionEngineId: '7a56a57f-366e-4b96-a2c7-8f1caa64f663',
        // Redact Document OCR engine - runs between the PDF converter
        // and PII detection, writing an invisible text layer onto scanned pages
        // and republishing the PDF as the TDO's primary `media` asset. Gated on
        // its own `runOCR` option, independent of PII detection (see
        // buildDocumentRedactionTasksAndRoutes): a document can be made
        // searchable without being scanned for PII, and vice versa. Setting
        // this to '' in the data-center config removes the stage entirely,
        // which is the rollback lever.
        documentOcrEngineId: '286e9f26-c865-4251-9e82-55aa62fb86c9',
      },
      pick(config, [
        'apiRoot',
        'defaultClusterId',

        'glcIngestionEngineId',
        'fastChunkerEngineId',

        'redactEngineId',

        'downloadEngineCategoryId',
        'downloadEngineId',

        'opticalTrackingEngineCatagoryId',
        'opticalTrackingEngineCategoryId',
        'opticalTrackingEngineId',

        'detectionCategory',
        'detectionEngineId',
        'detectionEngineGPU',

        'transcriptionCategoryId',
        'transcriptionEngineId',
        'speakerDetectionCategoryId',
        'pdfConverterEngineId',
        'documentPIIDetectionEngineId',
        'documentOcrEngineId',
        'poiDetectionEngineId',

        // 'voiceMaskEngineId',
      ]),
      applicationConfigOverrides,
      { transcriptionEngineOptions }
    );

    // now choose which detectionEngine to use as the default

    const detectionEngineCPU = combined.detectionEngineId;
    detectionEngineGPU = combined.detectionEngineGPU;
    const detectionEngineIds = [detectionEngineCPU];
    if (combined.detectionEngineGPU) {
      detectionEngineIds.push(combined.detectionEngineGPU);
    }
    return {
      ...combined,
      detectionEngineCPU,
      detectionEngineId:
        useGPU && detectionEngineGPU ? detectionEngineGPU : detectionEngineCPU, // pick default detectionEngineId to run
      detectionEngineIds,
    };
  }
);
